import { API_METHODS } from '@compass/client/constants/api-methods';
import type { GetMailboxInboxRequest, MailboxMessage } from '@compass/client/types/mailbox';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import { mailboxService } from '@/services/api';
import { apiClient, getApiPath } from '@/services/api.config';
import { queryKeys } from '@/services/query.config';
import { currentUser } from '@/utils/user';

/**
 * Hook to fetch the current user's mailbox inbox with cursor-based pagination.
 *
 * @param params - Pagination and filter params (limit, start_after_id, is_read)
 * @param options - Additional options
 * @param options.enabled - Whether the query should execute (default: true)
 */
export function useMailboxInbox(params?: GetMailboxInboxRequest, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.mailbox.inbox(params),
    queryFn: () => mailboxService.getMailboxInbox(params ?? {}),
    staleTime: 10 * 60 * 1000, // 10 minutes (SSE handles real-time updates)
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch the current user's unread message count.
 * Invalidated automatically by SSE events.
 */
export function useMailboxUnreadCount(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.mailbox.unreadCount(),
    queryFn: () => mailboxService.getMailboxUnreadCount({}),
    staleTime: 60 * 1000, // 1 minute (SSE invalidates on changes)
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to toggle the read/unread status of a mailbox message.
 * Optimistically updates the inbox cache.
 */
export function useUpdateMailboxFlags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId, isRead }: { messageId: string; isRead: boolean }) =>
      mailboxService.updateMailboxMessageFlags({ message_id: messageId, is_read: isRead }),
    onMutate: async ({ messageId, isRead }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: queryKeys.mailbox.all });

      // Snapshot previous inbox data for rollback
      const previousData = queryClient.getQueriesData({ queryKey: queryKeys.mailbox.all });

      // Optimistically update inbox cache entries
      queryClient.setQueriesData<{ messages?: MailboxMessage[]; has_more?: boolean }>(
        { queryKey: queryKeys.mailbox.all },
        old => {
          if (!old?.messages) return old;
          return {
            ...old,
            messages: old.messages.map(msg =>
              msg.id === messageId ? { ...msg, is_read: isRead } : msg,
            ),
          };
        },
      );

      return { previousData };
    },
    onError: (_err, _vars, context) => {
      // Roll back on error
      if (context?.previousData) {
        for (const [key, data] of context.previousData) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.mailbox.all });
    },
  });
}

/**
 * Hook to bulk delete mailbox messages.
 * Optimistically removes messages from the inbox cache.
 */
export function useBulkDeleteMailbox() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageIds: string[]) =>
      mailboxService.bulkDeleteMailboxMessages({ message_ids: messageIds }),
    onMutate: async messageIds => {
      await queryClient.cancelQueries({ queryKey: queryKeys.mailbox.all });

      const previousData = queryClient.getQueriesData({ queryKey: queryKeys.mailbox.all });

      const idsToDelete = new Set(messageIds);
      queryClient.setQueriesData<{ messages?: MailboxMessage[]; has_more?: boolean }>(
        { queryKey: queryKeys.mailbox.all },
        old => {
          if (!old?.messages) return old;
          return {
            ...old,
            messages: old.messages.filter(msg => !idsToDelete.has(msg.id ?? '')),
          };
        },
      );

      return { previousData };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        for (const [key, data] of context.previousData) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.mailbox.all });
    },
  });
}

/** Parsed SSE event from the notification stream. */
interface ParsedSSEEvent {
  id: string;
  type: string;
  data: string;
}

/** Mutable state for incremental SSE line parsing. */
interface SSEParserState {
  eventType: string;
  eventId: string;
  eventData: string;
}

/** Parse a single SSE line, emitting a complete event when an empty line is encountered. */
function parseSSELine(
  line: string,
  state: SSEParserState,
  onEvent: (evt: ParsedSSEEvent) => void,
): void {
  if (line.startsWith('id: ')) {
    state.eventId = line.slice(4).trim();
  } else if (line.startsWith('event: ')) {
    state.eventType = line.slice(7).trim();
  } else if (line.startsWith('data: ')) {
    state.eventData = line.slice(6);
  } else if (line === '' && state.eventId && state.eventData) {
    onEvent({ id: state.eventId, type: state.eventType, data: state.eventData });
    state.eventType = '';
    state.eventId = '';
    state.eventData = '';
  }
}

/**
 * Read SSE events from a ReadableStream and invoke the callback for each complete event.
 * Handles buffering of partial chunks and SSE field parsing.
 */
async function readSSEEvents(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  signal: AbortSignal,
  onEvent: (evt: ParsedSSEEvent) => void,
): Promise<void> {
  const decoder = new TextDecoder();
  const state: SSEParserState = { eventType: '', eventId: '', eventData: '' };
  let buffer = '';

  while (!signal.aborted) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      parseSSELine(line, state, onEvent);
    }
  }
}

/**
 * Prune a Set to keep it bounded. Removes the oldest half (by lexicographic sort)
 * when the set exceeds the given limit.
 */
function pruneSeenSet(seen: Set<string>, limit: number): void {
  if (seen.size <= limit) return;
  const sorted = [...seen].sort();
  for (const id of sorted.slice(0, Math.floor(limit / 2))) {
    seen.delete(id);
  }
}

/** Mailbox SSE event types */
export type MailboxEventType =
  | 'mailbox.message.received'
  | 'mailbox.message.sent'
  | 'mailbox.message.read'
  | 'mailbox.message.deleted'
  | 'mailbox.message.moved'
  | 'mailbox.mark_all_read';

/**
 * Hook that opens an SSE connection to the mailbox notification stream.
 *
 * Uses fetch (not EventSource) so we can send the Authorization header,
 * matching how all other dashboard API calls authenticate.
 *
 * On each event, invalidates the React Query inbox cache so the UI reflects
 * the latest state. Reconnects automatically with the last seen event ID
 * for backfill of missed events.
 *
 * Events may arrive out of order during reconnection. The inbox query
 * returns server-sorted data, so invalidation is sufficient — no client-side
 * reordering needed. Deduplication uses a bounded Set of seen event IDs.
 *
 * @param enabled - Whether to open the SSE connection (default: true)
 * @param onEvent - Optional callback for each event (for toast notifications, etc.)
 */
export function useMailboxNotifications(
  enabled = true,
  onEvent?: (type: MailboxEventType, data: Record<string, unknown>) => void,
) {
  const queryClient = useQueryClient();
  const seenRef = useRef(new Set<string>());
  const lastEventIdRef = useRef('');
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const updateInboxFromEvent = useCallback(
    (type: string, data: Record<string, unknown>) => {
      if (type === 'mailbox.message.received') {
        // New message received — prepend to inbox cache as a stub so the UI
        // shows the notification immediately. Then invalidate so a background
        // refetch replaces the stub with the full message (including metadata
        // needed for deep-link navigation).
        const newMsg: MailboxMessage = {
          id: data.message_id as string,
          sender_id: data.sender_id as string,
          subject: data.subject as string,
          created_at: data.received_at as string,
          updated_at: data.received_at as string,
          is_read: false,
          folder_id: '__inbox',
          metadata: (data.metadata as Record<string, unknown>) ?? undefined,
        };
        queryClient.setQueriesData<{ messages?: MailboxMessage[]; has_more?: boolean }>(
          { queryKey: queryKeys.mailbox.all },
          old => {
            if (!old?.messages) return { messages: [newMsg], has_more: false };
            // Avoid duplicate if already in list
            if (old.messages.some(m => m.id === newMsg.id)) return old;
            return { ...old, messages: [newMsg, ...old.messages] };
          },
        );
        // Refetch inbox so the stub is replaced with the full server message
        // (which includes metadata for deep-link navigation).
        queryClient.invalidateQueries({ queryKey: queryKeys.mailbox.all });
      } else if (type === 'mailbox.message.read') {
        // Mark message as read in cache
        const msgId = data.message_id as string;
        queryClient.setQueriesData<{ messages?: MailboxMessage[]; has_more?: boolean }>(
          { queryKey: queryKeys.mailbox.all },
          old => {
            if (!old?.messages) return old;
            return {
              ...old,
              messages: old.messages.map(m => (m.id === msgId ? { ...m, is_read: true } : m)),
            };
          },
        );
      } else if (type === 'mailbox.message.moved' || type === 'mailbox.message.deleted') {
        // Remove message from inbox cache
        const msgId = data.message_id as string;
        queryClient.setQueriesData<{ messages?: MailboxMessage[]; has_more?: boolean }>(
          { queryKey: queryKeys.mailbox.all },
          old => {
            if (!old?.messages) return old;
            return {
              ...old,
              messages: old.messages.filter(m => m.id !== msgId),
            };
          },
        );
      } else if (type === 'mailbox.mark_all_read') {
        // Mark all messages as read in cache
        queryClient.setQueriesData<{ messages?: MailboxMessage[]; has_more?: boolean }>(
          { queryKey: queryKeys.mailbox.all },
          old => {
            if (!old?.messages) return old;
            return {
              ...old,
              messages: old.messages.map(m => ({ ...m, is_read: true })),
            };
          },
        );
      }

      // Invalidate unread count for any event that changes read state
      queryClient.invalidateQueries({ queryKey: queryKeys.mailbox.unreadCount() });
    },
    [queryClient],
  );

  useEffect(() => {
    if (!enabled) return;

    const abortController = new AbortController();
    const seen = seenRef.current;

    async function connect() {
      const basePath = getApiPath(API_METHODS.MailboxService.StreamMailboxNotifications);
      const baseURL = apiClient.defaults.baseURL ?? '';
      const lastId = lastEventIdRef.current;
      const fullPath = `${baseURL}${basePath}`;
      const url = lastId ? `${fullPath}?last_event_id=${lastId}` : fullPath;

      const headers: Record<string, string> = { Accept: 'text/event-stream' };
      const user = currentUser();
      if (user?.access_token) {
        headers['Authorization'] = `Bearer ${user.access_token}`;
      }

      try {
        const response = await fetch(url, {
          headers,
          credentials: 'include',
          signal: abortController.signal,
        });
        if (!response.ok || !response.body) return;

        await readSSEEvents(response.body.getReader(), abortController.signal, evt => {
          lastEventIdRef.current = evt.id;
          if (seen.has(evt.id)) return;
          seen.add(evt.id);
          pruneSeenSet(seen, 500);

          try {
            const data = JSON.parse(evt.data) as Record<string, unknown>;
            updateInboxFromEvent(evt.type, data);
            onEventRef.current?.(evt.type as MailboxEventType, data);
          } catch {
            // Ignore parse errors
          }
        });
      } catch {
        // AbortError or network error — ignore
      }

      // Reconnect after 3s unless aborted
      if (!abortController.signal.aborted) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        if (!abortController.signal.aborted) connect();
      }
    }

    connect();

    return () => {
      abortController.abort();
    };
  }, [enabled, updateInboxFromEvent]);
}
